"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import Footer from "@/components/Footer";
import { SpeedInsights } from "@vercel/speed-insights/next";
import { isSimulatorRoute } from "@/lib/simulatorRoutes";

/**
 * Wraps the page content. On the Coach Split module routes and the persona
 * chat runner the simulator owns the full viewport directly under the NavMenu
 * — no centered container, no padding, no footer — so it can render the
 * immersive full-height layout. Every other route keeps the standard
 * centered, padded container + footer.
 *
 * Only the height-locked simulator runners belong here. Other /simulator
 * pages (module picker, personas playbook, phone, results) are normal
 * scrolling pages — locking them to the viewport clips their content below
 * the fold with no way to scroll to it.
 *
 * The route matcher lives in @/lib/simulatorRoutes so NavMenu can react to
 * the same "in a simulation" condition (it collapses the top nav there).
 */

// Immersive, edge-to-edge routes that still scroll normally (unlike the locked
// simulator). These manage their own full-bleed background, so the standard
// centered/padded container would frame them with an unwanted white border.
const FULL_BLEED_ROUTES = [
  "/faq-tests/assignments/",
  "/glossary-tests/assignments/",
];

export default function AppShell({ children, hideFooter = false }) {
  const pathname = usePathname() || "";
  const isSimulator = isSimulatorRoute(pathname);
  const isFullBleed = FULL_BLEED_ROUTES.some((route) =>
    pathname.startsWith(route)
  );
  // The home dashboard paints its own full-bleed cream background, so the
  // standard centered/padded container would frame it with a white border.
  const isHome = pathname === "/";
  const isConversationAnalyzer = pathname.endsWith("/conversation-analyzer");
  const shouldHideFooter = hideFooter || isConversationAnalyzer;

  // Lock the whole html → body → app-root chain to a definite viewport height
  // on simulator routes so the conversation column scrolls internally instead
  // of growing past the viewport bottom (see globals.css `.simulator-locked`).
  useEffect(() => {
    if (!isSimulator) return;
    const html = document.documentElement;
    html.classList.add("simulator-locked");
    return () => html.classList.remove("simulator-locked");
  }, [isSimulator]);

  if (isSimulator) {
    return (
      <main
        data-simulator-route
        className="flex min-h-0 grow flex-col overflow-hidden"
      >
        {children}
        <SpeedInsights debug={false} />
      </main>
    );
  }

  // Edge-to-edge, no centered container/padding/footer — the page owns its own
  // full-width background (e.g. the test-taking experience).
  if (isFullBleed) {
    return (
      <main className="flex grow flex-col">
        {children}
        <SpeedInsights debug={false} />
      </main>
    );
  }

  // Edge-to-edge home dashboard (its own cream background), but keep the footer.
  if (isHome) {
    return (
      <>
        <main className="flex grow flex-col">
          {children}
          <SpeedInsights debug={false} />
        </main>
        {!shouldHideFooter && <Footer />}
      </>
    );
  }

  return (
    <>
      <main className="flex grow flex-col">
        <div className="container mx-auto flex w-full grow flex-col px-4 py-4 md:px-6 md:py-12 app-content-spacing">
          {children}
        </div>
        <SpeedInsights debug={false} />
      </main>
      {!shouldHideFooter && <Footer />}
    </>
  );
}
